import { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  Input,
  TabList,
  Tab,
  Spinner,
  tokens,
} from '@fluentui/react-components';
import { SearchRegular } from '@fluentui/react-icons';
import { type UseMutationResult } from '@tanstack/react-query';
import { type TaxReturn, type ContactInfo } from '../../../types';
import { searchDataverse } from '../../../services/api';

export interface AddSpouseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentAliases: string[];
  updateMutation: UseMutationResult<unknown, Error, Partial<TaxReturn>>;
}

export function AddSpouseDialog({
  open,
  onOpenChange,
  currentAliases,
  updateMutation,
}: AddSpouseDialogProps) {
  const [mode, setMode] = useState<'search' | 'manual'>('search');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<ContactInfo[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedContact, setSelectedContact] = useState<ContactInfo | null>(null);

  // Manual entry fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  useEffect(() => {
    if (open) {
      setMode('search');
      setSearchTerm('');
      setSearchResults([]);
      setSelectedContact(null);
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
    }
  }, [open]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    setSearching(true);
    try {
      const results = await searchDataverse(searchTerm, 'contact');
      setSearchResults(results.contacts || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSave = () => {
    let spouseName: string;
    let spouseId: string | undefined;
    let spouseEmail: string | undefined;
    let spousePhone: string | undefined;

    if (mode === 'search' && selectedContact) {
      spouseName = selectedContact.fullName;
      spouseId = selectedContact.contactId;
      spouseEmail = selectedContact.email;
      spousePhone = selectedContact.phone || selectedContact.mobilePhone;
    } else if (mode === 'manual' && (firstName.trim() || lastName.trim())) {
      spouseName = `${firstName.trim()} ${lastName.trim()}`.trim();
      spouseEmail = email.trim() || undefined;
      spousePhone = phone.trim() || undefined;
    } else {
      return;
    }

    const updatedAliases = [...(currentAliases || [])];
    if (!updatedAliases.includes(spouseName)) {
      updatedAliases.push(spouseName);
    }

    const updates: Partial<TaxReturn> = {
      spouseName,
      spouseId,
      spouseEmail,
      spousePhone,
      filingType: 'joint',
      searchableAliases: updatedAliases,
    };

    updateMutation.mutate(updates, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  };

  const canSave =
    (mode === 'search' && selectedContact !== null) ||
    (mode === 'manual' && (firstName.trim() || lastName.trim()));

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface style={{ maxWidth: '520px' }}>
        <DialogBody>
          <DialogTitle>Add Spouse</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <TabList
                selectedValue={mode}
                onTabSelect={(_, data) => setMode(data.value as 'search' | 'manual')}
              >
                <Tab value="search">Search Contacts</Tab>
                <Tab value="manual">Manual Entry</Tab>
              </TabList>

              {mode === 'search' && (
                <>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Input
                      style={{ flex: 1 }}
                      placeholder="Search by name..."
                      value={searchTerm}
                      onChange={(_, data) => setSearchTerm(data.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      contentBefore={<SearchRegular />}
                    />
                    <Button
                      appearance="primary"
                      onClick={handleSearch}
                      disabled={searching || !searchTerm.trim()}
                    >
                      {searching ? <Spinner size="tiny" /> : 'Search'}
                    </Button>
                  </div>
                  {searchResults.length > 0 && (
                    <div style={{ maxHeight: '200px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {searchResults.map((contact) => (
                        <div
                          key={contact.contactId}
                          onClick={() => setSelectedContact(contact)}
                          style={{
                            padding: '12px',
                            backgroundColor: selectedContact?.contactId === contact.contactId ? '#e6f2ff' : 'var(--tl-bg-page)',
                            border: selectedContact?.contactId === contact.contactId ? '2px solid #0078d4' : '1px solid var(--tl-border)',
                            borderRadius: '6px',
                            cursor: 'pointer',
                          }}
                        >
                          <div style={{ fontWeight: 500 }}>{contact.fullName}</div>
                          <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)' }}>
                            {contact.email || 'No email'} {contact.phone && `• ${contact.phone}`}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {searchResults.length === 0 && searchTerm && !searching && (
                    <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)', textAlign: 'center', padding: '16px' }}>
                      No contacts found. Try a different search or use manual entry.
                    </div>
                  )}
                </>
              )}

              {mode === 'manual' && (
                <>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <Field label="First Name" style={{ flex: 1 }}>
                      <Input
                        value={firstName}
                        onChange={(_, data) => setFirstName(data.value)}
                        placeholder="First name"
                      />
                    </Field>
                    <Field label="Last Name" style={{ flex: 1 }}>
                      <Input
                        value={lastName}
                        onChange={(_, data) => setLastName(data.value)}
                        placeholder="Last name"
                      />
                    </Field>
                  </div>
                  <Field label="Email">
                    <Input
                      value={email}
                      onChange={(_, data) => setEmail(data.value)}
                      placeholder="spouse@email.com"
                      type="email"
                    />
                  </Field>
                  <Field label="Phone">
                    <Input
                      value={phone}
                      onChange={(_, data) => setPhone(data.value)}
                      placeholder="(239) 555-0123"
                    />
                  </Field>
                </>
              )}

              <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)', padding: '8px', backgroundColor: 'var(--tl-bg-subtle)', borderRadius: '4px' }}>
                Adding a spouse will change the filing type to "Married Filing Jointly" and make the spouse's name searchable.
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={!canSave || updateMutation.isPending}
              onClick={handleSave}
            >
              {updateMutation.isPending ? 'Saving...' : 'Add Spouse'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
